import { useCallback, useContext, useEffect, useImperativeHandle, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { unstable_batchedUpdates } from 'react-dom';
import AdmissionLocationView from '../components/AdmissionLocationView';
import {
  buildLocationCubicleUpdateParams,
  revampLocationCubicle,
  revampLocationPatient,
} from '../components/AdmissionLocationView/utils';
import { AdmissionContext } from '..';
import { eadmissionAPI } from '../../../apis';
import { alertMsg, ResponseCommCodeEnum } from '../../../apis/commlogic';
import { i18n } from '../../../language';
import TransferInPop from '../TransferIn/components/TransferOutPop';
import {
  buildUpdateTransferInParams,
  fetchTransferInPopupOptions,
  initTransferInPopupOptions,
} from '../TransferIn/transferInRequestUtil';

const SOPDView = ({ headerRef, contentHeight }) => {
  const { admissionParamsRef, showSOPDDetails } = useContext(AdmissionContext);
  const [queuebarList, setQueuebarList] = useState([]);
  const [cubicleList, setCubicleList] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [transferInPopOpen, setTransferInPopOpen] = useState(false);
  const [transferInEditData, setTransferInEditData] = useState(null);
  const [transferInPopupOptions, setTransferInPopupOptions] = useState(initTransferInPopupOptions);

  const revampPatient = useCallback(
    async (patient) => {
      return revampLocationPatient({ patient, isLogin: loginInfo.flag });
    },
    [loginInfo.flag]
  );

  const revampCubicle = useCallback(
    async (cubicle) => {
      return revampLocationCubicle({ cubicle, revampPatient });
    },
    [revampPatient]
  );

  const getCubicleDetail = useCallback(async () => {
    try {
      const { result } = await eadmissionAPI.getSOPDCubicleDetail();
      const [newPatientList, newCubicleList] = await Promise.all([
        Promise.all(_.map(result?.queueBar, (item) => revampPatient(item))),
        Promise.all(_.map(result?.cubicles, (item) => revampCubicle(item))),
      ]);
      unstable_batchedUpdates(() => {
        setQueuebarList(newPatientList);
        setCubicleList(newCubicleList);
      });
    } catch (error) {}
  }, [revampPatient, revampCubicle]);

  const handleDeleteBedPatient = useCallback(
    async ({ cubicleId, bedData, cubicleVersion, setSelectedBed }) => {
      try {
        const params = {
          cubicleId,
          transferRequestId: bedData?.patientInfo?.eaId,
          version: cubicleVersion,
        };
        await eadmissionAPI.assignOrDelSOPDPatient(params);
        await getCubicleDetail();
        setSelectedBed(null);
      } catch (error) {}
    },
    [getCubicleDetail]
  );

  const handleAssignPatient = useCallback(
    async ({ selectedPatient, selectedBed, setSelectedPatient, setSelectedBed }) => {
      try {
        if (!selectedPatient || !selectedBed || selectedBed?.hasPatient || !selectedBed?.bedName) {
          return;
        }
        const params = {
          column: selectedBed?.column,
          cubicleId: selectedBed?.cubicleId,
          transferRequestId: selectedPatient?.eaId,
          row: selectedBed?.row,
          version: selectedBed?.cubicleVersion,
        };
        await eadmissionAPI.assignOrDelSOPDPatient(params);
        await getCubicleDetail();
        setSelectedPatient(null);
        setSelectedBed(null);
      } catch (error) {
        setSelectedPatient(null);
        setSelectedBed(null);
      }
    },
    [getCubicleDetail]
  );

  const handleUpdateCubicle = useCallback(
    async (cubicleData) => {
      try {
        const params = buildLocationCubicleUpdateParams(cubicleData);
        await eadmissionAPI.updateLandingCubicle(params);
        await getCubicleDetail();
      } catch (error) {}
    },
    [getCubicleDetail]
  );

  // 加载Transfer In弹窗需要的下拉资料。
  const loadTransferInPopupOptions = useCallback(async () => {
    const options = await fetchTransferInPopupOptions(loginInfo);
    setTransferInPopupOptions(options);
  }, [loginInfo.hospitalId, loginInfo.wardId]);

  const closeTransferInPop = useCallback(() => {
    setTransferInPopOpen(false);
    setTransferInEditData(null);
  }, []);

  // 打开queuebar病人的Transfer In弹窗。
  const handleOpenPatientDetail = useCallback(async (data) => {
    const patientInfo = data?.patientInfo ?? data;
    const transferRequestId = patientInfo?.transferRequestId ?? patientInfo?.eaId;
    if (!transferRequestId) {
      return;
    }

    try {
      const { result } = await eadmissionAPI.getTransferInDetail({ transferRequestId });
      setTransferInEditData(result ?? patientInfo);
      setTransferInPopOpen(true);
    } catch (error) {}
  }, []);

  // 保存SOPD View打开的Transfer In资料后刷新cubicle。
  const handleSubmitTransferIn = useCallback(
    async (requestVal) => {
      try {
        closeTransferInPop();
        await eadmissionAPI.updateTransferInRequest(buildUpdateTransferInParams(requestVal));
        await getCubicleDetail();
      } catch (error) {
        if (error.statusCode === ResponseCommCodeEnum.BED_VERSION) {
          alertMsg(
            i18n.t(
              'BEDSTATUS_THE_TRANSFER_IN_DETAIL_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_PLEAS_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_TRANSFER_IN_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
            ),
            _.uniqueId('warning_'),
            'warning'
          );
        }
      }
    },
    [closeTransferInPop, getCubicleDetail]
  );

  useEffect(() => {
    loadTransferInPopupOptions();
  }, [loadTransferInPopupOptions, refresh]);

  useImperativeHandle(admissionParamsRef, () => ({
    setRefresh,
  }));

  // 切换用户角色或ward时重新加载cubicle资料。
  useEffect(() => {
    getCubicleDetail();
  }, [getCubicleDetail, refresh, loginInfo]);

  return (
    <>
      <AdmissionLocationView
        headerRef={headerRef}
        contentHeight={contentHeight}
        queuebarList={queuebarList}
        cubicleList={cubicleList}
        handleAssignPatient={handleAssignPatient}
        handleDeleteBedPatient={handleDeleteBedPatient}
        handleOpenPatientDetail={handleOpenPatientDetail}
        handleUpdateCubicle={handleUpdateCubicle}
        queuebarStorageKey="SHOW_SOPD_VIEW_QUEUE_BAR"
        floorplanZoomStorageKey="SOPD_VIEW_FLOORPLAN_ZOOM"
        showDetails={showSOPDDetails}
        patientInfoViewType="SOPD"
      />
      {transferInPopOpen && (
        <TransferInPop
          open={transferInPopOpen}
          closePop={closeTransferInPop}
          title="T/I Req."
          allData={transferInEditData}
          saveHandle={handleSubmitTransferIn}
          transferInPopupOptions={transferInPopupOptions}
        />
      )}
    </>
  );
};

export default SOPDView;
